/**
 * main.tsx — frontend-v3 entry point (T304 backend-bound provider tree).
 *
 * v.1-D §7.21 provider order (load-bearing):
 *   AppErrorBoundary
 *     └─ PayloadAuthProvider                  (Payload credential session)
 *         └─ ConvexAuthProvider               (Convex Auth OAuth — D-012)
 *             └─ ConvexProvider               (Convex reactive client)
 *                 ├─ RouterProvider           (the route tree from src/routes)
 *                 └─ Toaster                  (Sonner — sibling, inside ConvexProvider)
 *
 * AppErrorBoundary is the outermost ring so a provider-construction error
 * surfaces as the full-screen 500 card rather than a blank page. Toaster is
 * a sibling of RouterProvider inside ConvexProvider so toasts can subscribe
 * to Convex queries (e.g. an action-failed toast referencing live data).
 *
 * Two window-level error listeners forward uncaught failures into the
 * structured logger so even non-React errors land in the observability pipe.
 *
 * T304 PORT-SITE FIX #2 — extended, did NOT clobber, the v.3 `main.tsx`
 * authored by T001. The mock-only boot has been replaced with the backend-
 * bound provider tree; the route table from `./routes` is unchanged.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ConvexProvider } from 'convex/react';
import { ConvexAuthProvider } from '@convex-dev/auth/react';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import { convex } from './lib/convex';
import { PayloadAuthProvider } from './lib/payloadAuth';
import { AppErrorBoundary } from './layouts/AppErrorBoundary';
import { router } from './routes';
import { logger } from './lib/logger';
import './styles/global.css';

window.addEventListener('unhandledrejection', (event) => {
  logger.error('unhandled_promise_rejection', {
    reason:
      event.reason instanceof Error
        ? event.reason.message
        : String(event.reason),
  });
});

window.addEventListener('error', (event) => {
  logger.error('window_error', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in document');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <PayloadAuthProvider>
        {/* ConvexAuthProvider already mounts the underlying Convex client
            with auth attached; wrapping ConvexProvider inside it shadows
            the auth context with an unauth'd one and breaks
            `useConvexAuth()` + every requireAuth-gated query. Use
            ConvexAuthProvider alone — `useQuery` resolves through it. */}
        <ConvexAuthProvider client={convex}>
          <RouterProvider router={router} />
          <Toaster position="bottom-right" richColors closeButton />
        </ConvexAuthProvider>
      </PayloadAuthProvider>
    </AppErrorBoundary>
  </React.StrictMode>,
);
